import { toast } from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  isAnnotationVersionConflict,
  isEpisodeNotInvalid,
  isInvalidReasonNotAcceptable,
  isTrainingCheckUnavailable,
  isTrainingLocked,
  getDatasetEpisodeReview,
  saveEpisodeReview,
  type ReviewOptions,
  type EpisodeSummary,
} from "@/api";
import { HatTextModal } from "@/components/modal";
import { isHttpClientError } from "@/http";
import type { EpisodeNav } from "../FileBrowser/useEpisodeNav";
import { EpisodeReviewPanel } from "./EpisodeReviewPanel";
import { episodeReviewDirtyStore, type PendingEpisodeReview } from "./episodeReviewDirtyStore";
import {
  commentExceedsLimit,
  draftFromRow,
  isDraftDirty,
  rebaseDraft,
  rowFromResponse,
  toReviewPayload,
  withComment,
  withExcluded,
  withTermAdded,
  withTermRemoved,
  withToggledAcceptance,
  withToggledFailure,
  withToggledTag,
  type EpisodeReviewDraft,
} from "./episodeAnnotationDraft";

/**
 * 공용 Dataset 상세의 선택 에피소드 리뷰 편집 패널.
 *
 * 선택한 에피소드를 다시 조회해 스냅샷과 최신 토큰을 받는다. 목록 행에는 스냅샷이 없다.
 * 저장은 `저장·검토 완료` 하나다. 바뀐 필드만 보내고 늘 검토 완료를 기록한다.
 * - 성공: 저장 전 목록 순서에서 다음 에피소드로 간다. 다음이 없으면 머물고 알린다.
 * - 이탈 확인에서 저장: 보류한 목적지로 간다.
 * - 409 충돌: 머물고, 사용자가 바꾼 필드는 입력을 지키고 나머지는 최신 값으로 바꾼다. 자동 재시도나
 *   병합은 하지 않는다.
 * active가 false이면 리뷰 폼을 제거한다. 초안과 이탈 확인은 이 컴포넌트가 계속 유지한다.
 * 데이터셋 메모는 여기 두지 않는다. 메모는 코멘트(에피소드 전역)와 귀속이 다르므로 브라우저 위
 * 별도 row 가 소유한다. 저장하지 않은 편집이 있을 때 다른 에피소드로 이동하려 하면
 * `nav.setChangeGuard` 로 전환을 멈추고 확인 다이얼로그를 띄운다.
 */
// 이탈 확인 다이얼로그의 키. Esc 는 dismiss(현 화면 유지)이므로 원복에 쓰지 않는다.
const LEAVE_REVERT_KEY = "r";
const LEAVE_SAVE_KEY_LABEL = "Enter";
const TRAINING_LOCKED_REASON =
  "Training이 진행 중이라 제외 여부는 바꿀 수 없습니다. Training이 끝난 뒤 다시 시도해 주세요.";
// 데이터셋을 연 시점의 힌트다. 그 뒤 학습이 끝났을 수 있으므로 단정하지 않는다.
const TRAINING_IN_USE_HINT =
  "이 Dataset으로 Training이 진행 중일 수 있습니다. 제외 여부를 바꾸면 저장이 거절될 수 있습니다.";
// 독은 에피소드마다 다시 마운트된다. 그래서 이 에피소드를 다시 열면 잠금이 풀린다.
const TRAINING_CHECK_UNAVAILABLE_REASON =
  "Training 사용 여부를 확인하지 못해 지금은 제외 여부를 바꿀 수 없습니다. 잠시 후 이 에피소드를 다시 열어 시도해 주세요.";

export type DatasetEpisodeAnnotationPanelProps = {
  active?: boolean;
  datasetUuid: string;
  episodeUuid: string;
  activeRow: EpisodeSummary;
  options: ReviewOptions;
  loading: boolean;
  // 학습이 이 Dataset 을 쓰는 중이다(데이터셋을 연 시점의 힌트). 안내만 하고 막지 않는다. 막는 것은
  // 저장 시 서버 판정(409 training_locked·503 training_check_unavailable)이다. 힌트는 다시 조회하지
  // 않아서 학습이 끝난 뒤에도 남을 수 있다.
  trainingLocked?: boolean;
  onSaved: (row: EpisodeSummary) => void;
  // 저장 뒤 이동과 미저장 이탈 확인에 쓴다. 목록 앞뒤 이동은 에피소드 목록이 소유한다.
  nav: EpisodeNav;
};

export function DatasetEpisodeAnnotationPanel({
  active = true,
  datasetUuid,
  episodeUuid,
  activeRow,
  options,
  loading,
  trainingLocked = false,
  onSaved,
  nav,
}: DatasetEpisodeAnnotationPanelProps): ReactNode {
  const [row, setRow] = useState(activeRow);
  const [draft, setDraft] = useState<EpisodeReviewDraft>(() => draftFromRow(activeRow));
  const [saving, setSaving] = useState(false);
  const busyRef = useRef(false);
  // 저장이 끝나고 다음 행을 찾는 동안이다. 이 사이의 두 번째 저장은 같은 에피소드를 다시 완료한다.
  // 이 값은 이탈 가드에 쓰지 않는다. 가드가 막으면 다음 행으로 갈 수 없다.
  const advancingRef = useRef(false);
  const generation = useRef(0);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const [conflicted, setConflicted] = useState(false);
  // 저장이 제외 변경 때문에 거절되었다(409 training_locked·503 training_check_unavailable). 값은
  // 잠근 사유다. 제외 토글은 이 값으로만 잠근다.
  const [saveLockReason, setSaveLockReason] = useState<string | null>(null);
  // 가드가 보류한 전환. 사용자가 저장하거나 버리기로 하면 이 함수를 호출해 이동을 마친다.
  const [pendingLeave, setPendingLeave] = useState<{ apply: () => void } | null>(null);
  const dirty = isDraftDirty(draft, row);
  // 가드는 전환 시점에 호출되므로 최신 dirty 를 ref 로 읽는다(재등록 없이).
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  // 응답이 올 때 편집의 기준인 저장값. 재조회 결과에 편집을 다시 얹을 때 쓴다.
  const rowRef = useRef(row);
  rowRef.current = row;
  const commentTooLong = commentExceedsLimit(draft, row, options.review_comment_max_length);

  // 목록에는 스냅샷 비교가 없다. 선택한 한 건만 조회하며 늦은 응답은 새 선택을 덮지 않는다.
  // 받은 행에는 409 뒤와 같은 규칙으로 편집을 얹는다. 사용자가 바꾼 필드만 지키고 나머지는 최신
  // 값을 쓴다. 편집을 통째로 지키면 손대지 않은 필드가 옛 값인 채 바뀐 필드로 잡힌다.
  useEffect(() => {
    const current = ++generation.current;
    setDetailLoading(true);
    setDetailError(false);
    void getDatasetEpisodeReview(datasetUuid, episodeUuid)
      .then((next) => {
        if (generation.current !== current) return;
        const base = rowRef.current;
        setRow(next);
        setDraft((previous) => rebaseDraft(previous, base, next));
      })
      .catch(() => {
        if (generation.current === current) setDetailError(true);
      })
      .finally(() => {
        if (generation.current === current) setDetailLoading(false);
      });
    return () => {
      generation.current += 1;
    };
  }, [datasetUuid, episodeUuid, reloadKey]);

  const changeGuard = useCallback((apply: () => void): boolean => {
    if (busyRef.current) return false;
    if (!dirtyRef.current) {
      return true;
    }
    setPendingLeave({ apply });
    return false;
  }, []);

  const setChangeGuard = nav.setChangeGuard;
  useEffect(() => {
    setChangeGuard(changeGuard);
    return () => setChangeGuard(null);
  }, [setChangeGuard, changeGuard]);

  // 이탈 확인이 열려 있는 동안의 키. 모달이 열리면 독 자체 단축키는 inert 로 잠기므로 겹치지 않는다.
  // 최신 핸들러를 ref 로 읽는다. 그래서 draft 가 바뀔 때마다 리스너를 다시 등록하지 않는다.
  const leaveKeysRef = useRef({ busy: saving, onSave: () => {}, onRevert: () => {} });
  useEffect(() => {
    if (pendingLeave === null) {
      return;
    }
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.defaultPrevented || leaveKeysRef.current.busy) return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.key === "Enter") {
        event.preventDefault();
        leaveKeysRef.current.onSave();
        return;
      }
      if (event.key.toLowerCase() === LEAVE_REVERT_KEY) {
        event.preventDefault();
        leaveKeysRef.current.onRevert();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [pendingLeave]);

  // 창 이탈 가드가 읽을 수 있게 등록한다. 닫기 버튼은 창 셸에 있고 편집은 여기 있어서 둘은 형제다.
  // dirty 여부만으로는 무엇을 버리는지 물을 수 없다. 그래서 되돌리기 명령도 함께 등록한다.
  // 언마운트되면 이 화면의 미저장 편집도 사라지므로 등록도 지운다.
  const pendingReviewRef = useRef<PendingEpisodeReview>({ revert: () => {} });
  useEffect(() => {
    episodeReviewDirtyStore.set(datasetUuid, dirty || saving ? pendingReviewRef.current : null);
  }, [datasetUuid, dirty, saving]);
  useEffect(() => {
    return () => episodeReviewDirtyStore.set(datasetUuid, null);
  }, [datasetUuid]);

  // 옵션을 불러오는 동안에는 Parquet 미리보기만 표시한다.
  if (loading) {
    return null;
  }

  // 저장 실패 뒤 최신 행을 다시 읽고 입력을 그 위에 얹는다. 읽지 못하면 입력만 지킨다.
  const reloadAfterFailure = async (
    current: number,
    base: EpisodeSummary,
    note: string,
    keepAcceptance: boolean,
  ): Promise<void> => {
    try {
      const latest = await getDatasetEpisodeReview(datasetUuid, episodeUuid);
      if (generation.current !== current) return;
      setRow(latest);
      setDraft((previous) => {
        const rebased = rebaseDraft(previous, base, latest);
        // 수용할 수 없는 상태로 바뀌었으면 수용 입력을 최신 값으로 되돌린다. 그대로 두면 다음
        // 저장도 같은 409 로 실패한다.
        return keepAcceptance ? rebased : { ...rebased, accepted_invalid: latest.accepted_invalid };
      });
      setMessage(note);
    } catch {
      if (generation.current !== current) return;
      setDetailError(true);
      setMessage("최신 상태를 불러오지 못했습니다. 작성 내용은 유지했습니다.");
    }
  };

  const handleSaveError = async (
    error: unknown,
    current: number,
    base: EpisodeSummary,
  ): Promise<void> => {
    if (isAnnotationVersionConflict(error)) {
      setConflicted(true);
      await reloadAfterFailure(
        current,
        base,
        "다른 곳에서 먼저 저장되었습니다. 최신 내용을 불러왔고 작성 중인 편집은 유지했습니다. 확인한 뒤 다시 저장해 주세요.",
        true,
      );
      return;
    }
    if (isEpisodeNotInvalid(error)) {
      await reloadAfterFailure(
        current,
        base,
        "검증이 무효가 아니라서 결함을 수용할 수 없습니다. 최신 상태를 불러왔습니다.",
        false,
      );
      return;
    }
    if (isInvalidReasonNotAcceptable(error)) {
      await reloadAfterFailure(
        current,
        base,
        "채널 누락 에피소드는 결함을 수용할 수 없습니다. 최신 상태를 불러왔습니다.",
        false,
      );
      return;
    }
    if (isTrainingLocked(error)) {
      // 학습이 이 Dataset 을 쓰는 중이다. 제외 여부만 막힌다. 제외를 되돌리면 나머지는 저장된다.
      setSaveLockReason(TRAINING_LOCKED_REASON);
      toast.error(
        "이 Dataset으로 Training이 진행 중이라 제외 여부는 바꿀 수 없습니다. 제외를 되돌린 뒤 다시 저장해 주세요.",
      );
      return;
    }
    if (isTrainingCheckUnavailable(error)) {
      // 서버가 학습 사용 여부를 확인하지 못해 변경을 거부했다(fail-closed). 409 와 같이 제외 변경만
      // 막힌다. 제외를 되돌리면 나머지는 저장된다.
      setSaveLockReason(TRAINING_CHECK_UNAVAILABLE_REASON);
      toast.error(
        "Training 사용 여부를 확인하지 못해 제외 여부를 바꾸지 못했습니다. 제외를 되돌린 뒤 다시 저장해 주세요.",
      );
      return;
    }
    const serverMessage = isHttpClientError(error) ? error.serverMessage : null;
    toast.error(
      serverMessage ?? "에피소드 검토 내용을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.",
    );
  };

  /**
   * 저장·검토 완료. `after` 는 이탈 확인이 보류한 전환이다. 없으면 다음 에피소드로 간다.
   * 성공 여부를 돌려준다. 실패하면 그 자리에 머물고 입력을 지킨다.
   */
  const save = async (after?: () => void): Promise<boolean> => {
    if (busyRef.current || advancingRef.current) return false;
    if (detailLoading || detailError || loading || commentTooLong) return false;
    const current = generation.current;
    const base = row;
    busyRef.current = true;
    setSaving(true);
    setMessage(null);
    let saved: EpisodeSummary;
    try {
      const resp = await saveEpisodeReview(datasetUuid, episodeUuid, toReviewPayload(draft, base));
      if (generation.current !== current) return false;
      saved = rowFromResponse(base, resp);
    } catch (error) {
      if (generation.current === current) {
        busyRef.current = false;
        setSaving(false);
        await handleSaveError(error, current, base);
      }
      return false;
    }
    // 서버에 저장했으므로 잃을 편집이 없다. 다시 렌더하기 전에 다음 행으로 옮기므로 dirty 파생값이
    // 아직 갱신되지 않는다. 가드가 막지 않도록 ref 를 먼저 바꾼다.
    busyRef.current = false;
    dirtyRef.current = false;
    advancingRef.current = true;
    setSaving(false);
    setConflicted(false);
    setRow(saved);
    setDraft(draftFromRow(saved));
    // 성공 알림은 toast 로 띄우지 않는다. 리뷰에서는 Enter·S 를 연속으로 누르므로 알림이 쌓여
    // 화면을 덮는다. 목록 행의 짧은 플래시(onSaved)와 에피소드 이동이 확인을 대신한다.
    onSaved(saved);
    try {
      const result = await nav.afterSave(episodeUuid, after);
      if (generation.current === current && result === "end") {
        setMessage("검토를 완료했습니다. 이어서 볼 에피소드가 없습니다.");
      }
      if (generation.current === current && result === "failed") {
        setMessage(
          "검토를 완료했습니다. 다음 에피소드를 불러오지 못했습니다. 목록에서 다음 에피소드를 골라 주세요.",
        );
      }
    } finally {
      advancingRef.current = false;
    }
    return true;
  };

  // 편집을 저장값으로 되돌리고 그 자리에 머문다. 가드가 보류한 전환은 실행하지 않는다. 되돌린
  // 뒤 바로 이동하면 사용자가 "무엇을 지웠는지" 확인할 수 없다.
  const revertAndStay = (): void => {
    if (busyRef.current) return;
    setDraft(draftFromRow(row));
    setPendingLeave(null);
  };

  const saveThenLeave = (): void => {
    const leave = pendingLeave;
    setPendingLeave(null);
    void save(leave === null ? undefined : () => leave.apply());
  };

  leaveKeysRef.current = { busy: saving, onSave: saveThenLeave, onRevert: revertAndStay };
  // 창 이탈 가드가 호출할 명령. 사용자가 나가기를 고르면 이 편집을 저장값으로 되돌린다.
  pendingReviewRef.current = { revert: revertAndStay };

  return (
    <>
      {active ? (
        <EpisodeReviewPanel
          active={active}
          row={row}
          draft={draft}
          qualityTags={options.episode_quality_tags}
          commentMaxLength={options.review_comment_max_length}
          busy={saving}
          dirty={dirty}
          loading={detailLoading}
          loadError={detailError}
          message={message}
          conflicted={conflicted}
          excludedLocked={saveLockReason !== null}
          excludedNote={saveLockReason ?? (trainingLocked ? TRAINING_IN_USE_HINT : undefined)}
          onRetry={() => setReloadKey((value) => value + 1)}
          onSetExcluded={(excluded) => setDraft((current) => withExcluded(current, excluded))}
          onToggleFailure={() => setDraft((current) => withToggledFailure(current))}
          onToggleAcceptance={() => setDraft((current) => withToggledAcceptance(current))}
          onToggleTag={(tag) => setDraft((current) => withToggledTag(current, tag))}
          onAddTerm={(termKey, termValue) =>
            setDraft((current) => withTermAdded(current, termKey, termValue))
          }
          onRemoveTerm={(term) => setDraft((current) => withTermRemoved(current, term))}
          onSetComment={(comment) => setDraft((current) => withComment(current, comment))}
          onSave={() => void save()}
        />
      ) : null}
      {/* 미저장 편집이 있을 때 다른 에피소드로 이동하려 하면 표시한다. X·Esc·백드롭은 아무것도
          하지 않고 이 화면에 머문다. 닫으려던 사용자가 편집을 잃지 않게 dismiss 는 아무것도 바꾸지
          않아야 한다. "버리고 그냥 넘어가기"는 두지 않는다. 사용자는 되돌린 뒤 확인하거나, 검토를
          완료하고 이동한다. 리뷰 화면의 저장은 늘 검토 완료다. */}
      <HatTextModal
        open={pendingLeave !== null}
        label="저장하지 않은 검토 내용이 있습니다."
        caption="검토 완료하면 편집을 저장하고 검토 완료로 기록합니다. 원복하면 이 에피소드의 편집이 저장값으로 돌아갑니다."
        okText={`검토 완료하고 이동 (${LEAVE_SAVE_KEY_LABEL})`}
        cancelText={`원복 후 대기 (${LEAVE_REVERT_KEY.toUpperCase()})`}
        busy={saving}
        onOkPress={saveThenLeave}
        onCancelPress={revertAndStay}
        onDismiss={() => setPendingLeave(null)}
      />
    </>
  );
}
